/**
 * @fileoverview Comprehensive Signal Documentation
 * 
 * Institutional-quality documentation for all CAP.IO signals.
 * Used for: tooltips, glossary, signal detail views, research exports.
 * 
 * @module data/signalDocumentation
 */

// =============================================================================
// FLAGSHIP SIGNALS (5)
// =============================================================================

const FLAGSHIP_SIGNALS = {
  NET_LIQUIDITY: {
    id: 'NET_LIQUIDITY',
    name: 'Net Federal Liquidity',
    shortName: 'Net Liquidity',
    category: 'liquidity',
    tier: 'flagship',
    
    description: 'Net Federal Liquidity measures the effective quantity of dollar liquidity available to the financial system after accounting for Fed balance sheet operations and Treasury cash management.',
    
    calculation: {
      formula: 'WALCL − WTREGEN − RRPONTSYD',
      explanation: 'Federal Reserve Total Assets (WALCL) minus the Treasury General Account (WTREGEN) minus Overnight Reverse Repurchase Agreements (RRPONTSYD).',
      components: [
        { id: 'WALCL', name: 'Federal Reserve Total Assets', role: 'The Fed\'s balance sheet size - base liquidity injection' },
        { id: 'WTREGEN', name: 'Treasury General Account', role: 'Treasury\'s cash balance at the Fed - liquidity removed from system' },
        { id: 'RRPONTSYD', name: 'Reverse Repo Facility', role: 'Cash parked back at the Fed by money market funds - liquidity sterilized' },
      ],
    },
    
    whyItMatters: 'Net Liquidity represents the actual dollars available to circulate through the financial system and chase risk assets. While the Fed\'s balance sheet (WALCL) captures gross liquidity creation, two critical drains must be subtracted: the Treasury General Account (government cash holdings) and the Reverse Repo Facility (excess cash parked at the Fed). This metric gained prominence post-2020 when unprecedented fiscal and monetary interventions created large, volatile swings in all three components.',
    
    marketMechanism: 'Liquidity flows through the system via bank reserves. When Net Liquidity rises, reserves increase, funding conditions ease, and risk appetite expands. Dealers have more balance sheet capacity, repo rates stay anchored, and the "reach for yield" intensifies. Conversely, falling Net Liquidity tightens financial conditions even if the Fed hasn\'t changed policy rates - a stealth tightening that often catches markets off guard.',
    
    interpretation: {
      rising: 'Expansionary for risk assets. Historically correlates strongly with equity market appreciation, credit spread compression, and increased speculative activity. The 2020-2021 bull market coincided with Net Liquidity expanding from ~$3.5T to over $6T.',
      falling: 'Contractionary headwind for risk assets. Equity markets have shown sensitivity to Net Liquidity drawdowns, particularly when declines are rapid or unexpected. The 2022 bear market occurred alongside a ~$1.5T reduction in Net Liquidity via QT and RRP growth.',
      stable: 'Neutral liquidity backdrop. Other factors (earnings, policy rates, geopolitics) may dominate price action.',
    },
    
    leadLag: 'coincident',
    leadLagNotes: 'Net Liquidity is primarily a coincident indicator. Changes in liquidity conditions transmit to markets relatively quickly through funding markets and dealer balance sheets. However, scheduled events (debt ceiling resolutions, quarterly tax payments) can make future liquidity shifts somewhat predictable.',
    
    typicalRange: {
      current: '$5.5T - $6.5T',
      historicalLow: '~$3.2T (pre-COVID)',
      historicalHigh: '~$6.3T (late 2021)',
      notes: 'Range has shifted dramatically higher post-2020',
    },
    
    keyLevels: [
      { level: '$6.0T', significance: 'Approximate level associated with easy conditions post-2020' },
      { level: '$5.5T', significance: 'Level where tightening pressures emerged in 2022-2023' },
    ],
    
    historicalEvents: [
      { date: '2020-03-23', event: 'Fed announces unlimited QE', impact: 'Net Liquidity began explosive expansion from ~$3.5T' },
      { date: '2021-12', event: 'Net Liquidity peaks near $6.3T', impact: 'Coincided with speculative excess (meme stocks, crypto highs)' },
      { date: '2022-06', event: 'QT begins at $47.5B/month', impact: 'Net Liquidity decline accelerated, equity bear market deepened' },
      { date: '2023-01', event: 'Debt ceiling binds, TGA drawdown begins', impact: 'TGA drain offset QT, providing temporary liquidity relief' },
      { date: '2023-06', event: 'Debt ceiling resolved, TGA rebuild', impact: 'Rapid liquidity drain as Treasury rebuilt cash buffer' },
    ],
    
    tradingImplication: 'Net Liquidity serves as a key input for risk allocation decisions. Rising liquidity environments favor equity overweights, credit exposure, and duration extension. Falling liquidity favors defensive positioning, cash accumulation, and reduced leverage. Many systematic strategies incorporate liquidity metrics as regime indicators.',
    
    watchFor: [
      'Debt ceiling episodes: TGA drawdowns inject liquidity, resolutions drain it',
      'Quarter-end dynamics: Tax payments affect TGA; repo spikes affect RRP',
      'Fed policy shifts: QT pace changes directly impact base liquidity',
      'Money market fund behavior: Large RRP swings affect net figure',
    ],
    
    limitations: [
      'Does not capture private credit creation or shadow banking liquidity',
      'Weekly data frequency can miss intra-week volatility',
      'Global dollar liquidity (offshore) not included',
      'Correlation with equities not stable across all regimes',
    ],
    
    relatedSignals: [
      { id: 'WALCL', relationship: 'Component - Fed balance sheet (gross liquidity)' },
      { id: 'WTREGEN', relationship: 'Component - TGA (liquidity drain)' },
      { id: 'RRPONTSYD', relationship: 'Component - RRP (liquidity sterilization)' },
      { id: 'NET_LIQUIDITY_ZSCORE', relationship: 'Normalized version for cross-regime comparison' },
      { id: 'NET_LIQUIDITY_ROC', relationship: 'Rate of change - direction/momentum of liquidity' },
    ],
    
    dataProperties: {
      frequency: 'Weekly (Wednesday)',
      source: 'Federal Reserve H.4.1, Treasury, NY Fed',
      lag: '1-2 days after reference date',
      history: 'Components available from 2003; modern interpretation from 2020',
      units: 'USD (typically displayed in trillions)',
    },
    
    displayPreferences: {
      invertedLogic: false,
      preferredFormat: 'currency_trillions',
      chartType: 'area',
      colorLogic: 'higher_is_positive',
    },
  },

  CURVE_2S10S: {
    id: 'CURVE_2S10S',
    name: '2s10s Yield Curve Spread',
    shortName: '2s10s Curve',
    category: 'rates',
    tier: 'flagship',
    
    description: 'The difference between the 10-year and 2-year U.S. Treasury yields, representing the slope of the yield curve at the most economically significant tenor pair.',
    
    calculation: {
      formula: 'DGS10 − DGS2',
      explanation: '10-Year Treasury Constant Maturity Rate minus 2-Year Treasury Constant Maturity Rate.',
      components: [
        { id: 'DGS10', name: '10-Year Treasury Yield', role: 'Long-term rate reflecting growth and inflation expectations' },
        { id: 'DGS2', name: '2-Year Treasury Yield', role: 'Short-term rate reflecting Fed policy expectations' },
      ],
    },
    
    whyItMatters: 'The 2s10s spread is the most widely monitored yield curve measure and carries significant predictive power for economic recessions. The curve slope reflects the market\'s collective assessment of future growth, inflation, and monetary policy. Under normal conditions, longer-dated bonds yield more than shorter-dated bonds (positive slope) to compensate for duration risk. An inverted curve (negative spread) indicates markets expect deteriorating conditions and future rate cuts. The 2s10s specifically captures the tension between Fed policy (2-year) and long-term economic outlook (10-year). This inversion has preceded every U.S. recession since 1970 with one false positive (1966).',
    
    marketMechanism: 'The spread operates through multiple channels. Bank profitability suffers when the curve inverts (borrow short, lend long becomes unprofitable), tightening credit availability. Corporate CFOs see inversion as a warning to preserve cash. Consumer confidence typically weakens. The self-fulfilling nature of inversion expectations can accelerate the cycle.',
    
    interpretation: {
      positive: 'Healthy economic expectations. A spread of +100-250bps is historically normal during expansions. Steeper curves favor bank stocks, value equities, and cyclical sectors.',
      flat: 'Transition zone (+50bps to -25bps). Markets uncertain about growth trajectory. Often occurs during late-cycle tightening. Heightened volatility common.',
      inverted: 'Recession warning. Inversions below -25bps have preceded recessions with 6-24 month lead time. Duration of inversion matters - deeper and longer inversions correlate with more severe downturns.',
      resteepening: 'Critical phase. After inversion, rapid re-steepening often immediately precedes recession onset. This occurs as the Fed cuts rates in response to deterioration.',
    },
    
    leadLag: 'leading',
    leadLagNotes: 'The 2s10s is a leading indicator with variable lead time. Historical analysis shows inversions precede recessions by 6-24 months, with an average of approximately 12-14 months. The re-steepening phase is a shorter-lead signal, often just 0-6 months before recession onset.',
    
    typicalRange: {
      current: '-50bps to +100bps (late cycle)',
      expansionNormal: '+100bps to +250bps',
      historicalLow: '-108bps (1981, 2023)',
      historicalHigh: '+290bps (2011)',
    },
    
    keyLevels: [
      { level: '0bps', significance: 'Flat curve - transition from normal to inverted' },
      { level: '-25bps', significance: 'Mild inversion - recession probability rising significantly' },
      { level: '-50bps', significance: 'Deep inversion - strong recession signal' },
      { level: '-75bps', significance: 'Severe inversion - comparable to 1980, 2000, 2006-2007' },
    ],
    
    historicalEvents: [
      { date: '2006-07 to 2007-05', event: 'Pre-GFC inversion', impact: 'Curve inverted 10 months before Dec 2007 recession start' },
      { date: '2019-08', event: 'Brief inversion', impact: 'Preceded COVID recession, though causality debated' },
      { date: '2022-04', event: 'Inversion begins', impact: 'Most sustained inversion since 1980s, reached -108bps' },
      { date: '2023-07', event: 'Maximum inversion depth', impact: '-108bps, matching 1981 extreme' },
    ],
    
    tradingImplication: 'Curve positioning is a core fixed income strategy. Steepeners (long 2s, short 10s) profit from normalization but carry negative carry during inversion. Equity investors use curve shape as a regime indicator - inverted curves favor defensive positioning, quality factors, and reduced cyclical exposure.',
    
    watchFor: [
      'Rate of change matters - rapid moves more significant than level alone',
      'Fed pivot expectations - curve will steepen violently on cut expectations',
      'Real vs. nominal - compare with TIPS spreads for inflation component',
      'International curves - global synchronization amplifies signal',
    ],
    
    limitations: [
      'Lead time highly variable (6-24 months)',
      'QE distortions may have suppressed long-end yields artificially',
      'Term premium decomposition can give different readings',
      'One historical false positive (1966 - no recession)',
    ],
    
    relatedSignals: [
      { id: 'CURVE_3M10Y', relationship: 'Alternative curve measure preferred by some Fed researchers' },
      { id: 'DGS10', relationship: 'Component - long end of spread' },
      { id: 'DGS2', relationship: 'Component - short end of spread' },
      { id: 'CURVE_2S10S_ZSCORE', relationship: 'Normalized for cross-cycle comparison' },
      { id: 'REAL_10Y', relationship: 'Related - real rate component of long end' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Federal Reserve H.15 via FRED',
      lag: 'Same day (market close)',
      history: '1976-06-01 (consistent 2-year series start)',
      units: 'Percentage points (basis points commonly used)',
    },
    
    displayPreferences: {
      invertedLogic: true,
      preferredFormat: 'basis_points',
      chartType: 'line',
      colorLogic: 'context_dependent',
    },
  },

  HY_IG_SPREAD: {
    id: 'HY_IG_SPREAD',
    name: 'High Yield − Investment Grade Spread',
    shortName: 'HY-IG Spread',
    category: 'credit',
    tier: 'flagship',
    
    description: 'The difference between high yield (junk bond) and investment grade corporate bond option-adjusted spreads, measuring the additional compensation demanded for holding lower-quality credit.',
    
    calculation: {
      formula: 'BAMLH0A0HYM2 − BAMLC0A0CM',
      explanation: 'ICE BofA US High Yield Index OAS minus ICE BofA US Corporate Index OAS.',
      components: [
        { id: 'BAMLH0A0HYM2', name: 'ICE BofA US High Yield OAS', role: 'Spread on BB-rated and below corporate bonds' },
        { id: 'BAMLC0A0CM', name: 'ICE BofA US Corporate OAS', role: 'Spread on investment grade corporate bonds' },
      ],
    },
    
    whyItMatters: 'The HY-IG spread isolates credit risk appetite by removing the common components affecting all corporate bonds (duration risk, general rate expectations). What remains is the market\'s pure assessment of default risk and risk appetite. This measure captures the "reach for yield" dynamic that characterizes risk-on environments and the flight-to-quality that defines stress episodes.',
    
    marketMechanism: 'Credit spreads reflect the collective assessment of corporate default probability, recovery rates, and liquidity conditions. High yield issuers are more sensitive to economic conditions - they have less margin for error. When recession risk rises, HY spreads widen faster than IG spreads because: (1) default probabilities rise disproportionately for weaker credits, (2) liquidity evaporates in HY markets first, and (3) institutional mandates force selling of downgraded "fallen angels."',
    
    interpretation: {
      tight: 'Risk appetite strong. Spread below 250bps indicates aggressive reach for yield. Often coincides with late-cycle excess, covenant-lite issuance, and leveraged lending booms.',
      normal: 'Balanced risk assessment. Spread of 250-350bps reflects healthy credit differentiation without panic.',
      elevated: 'Stress emerging. Spread of 350-500bps indicates credit market concern. New HY issuance slows, refinancing becomes challenging.',
      stressed: 'Credit crisis conditions. Spread above 500bps signals severe stress. HY market effectively closed for new issuance.',
    },
    
    leadLag: 'coincident_to_slight_lead',
    leadLagNotes: 'Credit spreads typically move coincidentally with or slightly ahead of equity markets. Credit investors are often more conservative and fundamental-focused, sometimes identifying stress before equity markets react.',
    
    typicalRange: {
      current: '200-400bps',
      cycleLow: '~200bps (risk-on extremes)',
      cycleHigh: '~800bps (recessions)',
      crisisHigh: '1200-2000bps (2008, 2020)',
    },
    
    keyLevels: [
      { level: '250bps', significance: 'Tight - aggressive risk appetite' },
      { level: '350bps', significance: 'Normal equilibrium' },
      { level: '500bps', significance: 'Stressed - credit concerns elevated' },
      { level: '700bps', significance: 'Pre-crisis level - significant distress' },
    ],
    
    historicalEvents: [
      { date: '2007-06', event: 'Spreads begin widening from cycle lows', impact: 'Early warning before equity peak, Bear Stearns funds' },
      { date: '2008-12', event: 'Peak spread during GFC', impact: 'HY-IG spread exceeded 1500bps' },
      { date: '2016-02', event: 'Energy sector stress', impact: 'Spread reached ~650bps on oil collapse fears' },
      { date: '2020-03', event: 'COVID liquidity crisis', impact: 'Spread spiked to ~1000bps before Fed intervention' },
    ],
    
    tradingImplication: 'Credit spread positioning is implemented through cash bonds, CDS indices (CDX HY vs CDX IG), or ETF pairs (HYG vs LQD). Many equity investors monitor credit spreads as a confirmation signal - divergences between equity and credit markets often resolve in credit\'s favor.',
    
    watchFor: [
      'Dispersion within HY: CCC-B spread widening often leads broader HY',
      'New issuance calendar: Heavy supply can widen spreads technically',
      'Fallen angel risk: BBB downgrades to HY create forced selling',
      'Energy sector: Historically large HY weight, sensitive to oil',
    ],
    
    limitations: [
      'Index composition changes over time affect comparability',
      'OAS models have assumptions that may not hold in crisis',
      'Liquidity premium conflated with credit premium',
      'Sector concentration (energy, telecom) can dominate moves',
    ],
    
    relatedSignals: [
      { id: 'BAMLH0A0HYM2', relationship: 'Component - HY absolute spread' },
      { id: 'BAMLC0A0CM', relationship: 'Component - IG absolute spread' },
      { id: 'CCC_B_SPREAD', relationship: 'Related - quality spectrum within HY' },
      { id: 'HY_IG_SPREAD_ZSCORE', relationship: 'Normalized for historical comparison' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'ICE BofA Indices via FRED',
      lag: 'Same day',
      history: '1996-12-31',
      units: 'Basis points',
    },
    
    displayPreferences: {
      invertedLogic: false,
      preferredFormat: 'basis_points',
      chartType: 'line',
      colorLogic: 'higher_is_negative',
    },
  },

  VIX_TERM_SPREAD: {
    id: 'VIX_TERM_SPREAD',
    name: 'VIX Term Structure Spread',
    shortName: 'VIX Term Spread',
    category: 'volatility',
    tier: 'flagship',
    
    description: 'The difference between 3-month VIX (VIX3M) and spot VIX, measuring the shape of the implied volatility term structure for S&P 500 options.',
    
    calculation: {
      formula: 'VIX3M − VIXCLS',
      explanation: 'CBOE 3-Month Volatility Index minus CBOE Volatility Index (spot).',
      components: [
        { id: 'VIX3M', name: '3-Month VIX', role: 'Expected volatility over next 3 months' },
        { id: 'VIXCLS', name: 'VIX (Spot)', role: 'Expected volatility over next 30 days' },
      ],
    },
    
    whyItMatters: 'The VIX term structure reveals market expectations about the persistence of volatility. Under normal conditions, VIX3M exceeds spot VIX (positive spread, "contango") because uncertainty compounds over longer horizons. When the term structure inverts (negative spread, "backwardation"), it signals acute near-term fear exceeding longer-term concerns - typically during active selloffs or crisis periods. This measure captures something the VIX level alone cannot: whether elevated volatility is expected to be transitory or persistent.',
    
    marketMechanism: 'Options market makers and volatility traders continuously price expected variance across expiries. The term structure shape reflects supply/demand for hedging at different horizons. During selloffs, demand for near-term puts explodes as investors scramble for protection, pushing spot VIX higher relative to longer-dated vol.',
    
    interpretation: {
      positive_wide: 'Strong contango (+3 to +6 points). Complacent environment. Near-term calm expected to continue. Can represent vulnerability if shock arrives.',
      positive_normal: 'Mild contango (+1 to +3 points). Healthy vol environment. Normal mean-reversion expectations embedded.',
      flat: 'Transitional (−1 to +1 points). Uncertainty about vol direction. Often precedes or follows volatility spikes.',
      inverted: 'Backwardation (< −1 point). Active stress. Near-term fear exceeds longer-term. Deep inversion (< −3) indicates crisis conditions.',
    },
    
    leadLag: 'coincident_to_slight_lead',
    leadLagNotes: 'Term structure shifts often precede or coincide with equity selloffs. The structure typically inverts as selloffs begin and normalizes as they end.',
    
    typicalRange: {
      current: '+1 to +4 points',
      calmExtreme: '+6 to +8 points',
      inversionStart: '< 0',
      crisisInversion: '−3 to −10 points',
    },
    
    keyLevels: [
      { level: '+3', significance: 'Elevated contango - complacent, potential short-vol crowding' },
      { level: '0', significance: 'Flat structure - transitional, watch for direction' },
      { level: '−2', significance: 'Inverted - active stress, selloff likely underway' },
      { level: '−5', significance: 'Deep inversion - crisis conditions' },
    ],
    
    historicalEvents: [
      { date: '2018-02-05', event: 'Volmageddon', impact: 'Term structure inverted sharply as VIX doubled in hours' },
      { date: '2020-03', event: 'COVID crash', impact: 'Deep inversion exceeded −10 at VIX peak above 80' },
      { date: '2023-03', event: 'Regional bank crisis', impact: 'Inversion as SVB fears spiked, normalized within weeks' },
    ],
    
    tradingImplication: 'Term structure positioning is fundamental to volatility trading. VIX futures and ETPs (VXX, SVXY) have returns dominated by roll yield - contango benefits short-vol, backwardation benefits long-vol. Equity investors use term structure as a regime indicator: persistent contango supports buy-the-dip strategies, while backwardation suggests caution.',
    
    watchFor: [
      'Structure flattening during low VIX: potential complacency unwind setup',
      'Inversion without high spot VIX: unusual, may indicate forward-looking stress',
      'Rapid normalization after inversion: often marks selloff exhaustion',
      'Divergences between VIX and VVIX can add context',
    ],
    
    limitations: [
      'Dominated by S&P options - may miss sector-specific stress',
      'VIX calculation methodology changes over time',
      'Supply/demand technicals can distort signal',
      'Less reliable at extreme absolute VIX levels (>40)',
    ],
    
    relatedSignals: [
      { id: 'VIXCLS', relationship: 'Component - spot VIX level' },
      { id: 'VXVCLS', relationship: 'Related - VIX of VIX (uncertainty about vol)' },
      { id: 'VIX_TERM_SPREAD_ZSCORE', relationship: 'Normalized for historical comparison' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'CBOE via FRED',
      lag: 'Same day (market close)',
      history: '2007-12-04 (VIX3M inception)',
      units: 'Volatility points (percentage)',
    },
    
    displayPreferences: {
      invertedLogic: true,
      preferredFormat: 'decimal',
      chartType: 'line',
      colorLogic: 'context_dependent',
    },
  },

  CAP_STRESS_INDEX: {
    id: 'CAP_STRESS_INDEX',
    name: 'CAP Stress Index',
    shortName: 'Stress Index',
    category: 'composite',
    tier: 'flagship',
    
    description: 'A proprietary composite indicator synthesizing signals across liquidity, rates, credit, volatility, and macroeconomic domains into a single measure of financial system stress.',
    
    calculation: {
      formula: 'Weighted composite of constituent stress signals',
      explanation: 'The index combines normalized readings from multiple stress indicators, weighted by historical predictive power and theoretical importance. Each component is z-scored against its own history before aggregation.',
      components: [
        { category: 'Liquidity', weight: '25%', signals: ['NET_LIQUIDITY', 'WALCL', 'RRPONTSYD'] },
        { category: 'Rates', weight: '20%', signals: ['CURVE_2S10S', 'CURVE_3M10Y', 'REAL_10Y'] },
        { category: 'Credit', weight: '25%', signals: ['HY_IG_SPREAD', 'BAMLH0A0HYM2', 'BBB_TREASURY_SPREAD'] },
        { category: 'Volatility', weight: '15%', signals: ['VIXCLS', 'VIX_TERM_SPREAD', 'VXVCLS'] },
        { category: 'Macro', weight: '15%', signals: ['NFCI', 'STLFSI4', 'UMCSENT'] },
      ],
    },
    
    whyItMatters: 'Individual stress signals each capture a slice of financial conditions, but no single indicator provides a complete picture. The CAP Stress Index aggregates information across market segments to provide a holistic view of systemic stress. The composite approach also reduces false positives from idiosyncratic moves in individual markets.',
    
    marketMechanism: 'Financial stress propagates through interconnected channels. Liquidity stress impairs dealer intermediation, widening spreads. Credit stress raises funding costs, tightening conditions. Volatility spikes trigger risk reduction and margin calls. These channels feed back on each other, creating the potential for nonlinear stress amplification.',
    
    interpretation: {
      calm: 'Index below 25. Benign conditions across most components. Risk assets typically well-supported.',
      normal: 'Index 25-45. Mixed signals, some components showing mild stress. Normal market functioning.',
      elevated: 'Index 45-65. Multiple stress domains flashing warnings. Conditions deteriorating.',
      high: 'Index 65-80. Significant stress across multiple domains. Crisis dynamics potentially emerging.',
      crisis: 'Index above 80. Severe systemic stress. Historically rare - associated with major market events (2008, 2020).',
    },
    
    leadLag: 'coincident',
    leadLagNotes: 'The composite index is primarily coincident with market stress, reflecting current conditions rather than predicting future stress.',
    
    typicalRange: {
      calm: '15-30',
      normal: '30-45',
      elevated: '45-65',
      high: '65-80',
      crisis: '80-100',
    },
    
    keyLevels: [
      { level: '30', significance: 'Threshold between calm and normal conditions' },
      { level: '50', significance: 'Midpoint - elevated stress, increased caution' },
      { level: '70', significance: 'High stress - significant market disruption' },
      { level: '85', significance: 'Crisis conditions - extreme stress, rare historically' },
    ],
    
    historicalEvents: [
      { date: '2008-10', event: 'Global Financial Crisis peak', impact: 'Index would have reached 90+ (retrospective calculation)' },
      { date: '2020-03', event: 'COVID liquidity crisis', impact: 'Index spiked to 85+ before Fed intervention' },
      { date: '2022-06', event: 'Fed tightening / crypto collapse', impact: 'Index elevated to 55-60 range' },
      { date: '2023-03', event: 'Regional bank crisis', impact: 'Index briefly touched 65' },
    ],
    
    tradingImplication: 'The Stress Index serves as a regime indicator for portfolio construction. Low readings support equity overweights, credit exposure, and carry strategies. Elevated readings favor reduced risk, increased hedges, and cash accumulation.',
    
    watchFor: [
      'Rate of change: Rapid increases more concerning than gradual drift',
      'Component dispersion: Broad stress more concerning than single-component',
      'Persistence: Sustained elevation vs. brief spikes have different implications',
      'Divergences from price action: Index elevated while markets calm = potential vulnerability',
    ],
    
    limitations: [
      'Backward-looking components create some lag',
      'Weights calibrated to historical relationships that may shift',
      'Cannot anticipate truly novel stress events',
      'Composite may smooth over important single-signal extremes',
    ],
    
    relatedSignals: [
      { id: 'NFCI', relationship: 'Chicago Fed index - complementary institutional measure' },
      { id: 'STLFSI4', relationship: 'St. Louis Fed index - alternative composite' },
      { id: 'NET_LIQUIDITY', relationship: 'Component - liquidity domain' },
      { id: 'HY_IG_SPREAD', relationship: 'Component - credit domain' },
      { id: 'VIXCLS', relationship: 'Component - volatility domain' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Capitulators.io proprietary calculation',
      lag: '1 day (dependent on component data availability)',
      history: 'Calculated from 2010 (component availability)',
      units: 'Index (0-100 scale)',
    },
    
    displayPreferences: {
      invertedLogic: false,
      preferredFormat: 'integer',
      chartType: 'area',
      colorLogic: 'higher_is_negative',
    },
  },
};


// =============================================================================
// PRIMARY SIGNALS - Composites & Spreads (8)
// =============================================================================

const PRIMARY_SIGNALS = {
  REAL_10Y: {
    id: 'REAL_10Y',
    name: '10-Year Real Interest Rate',
    shortName: 'Real 10Y',
    category: 'rates',
    tier: 'primary',
    
    description: 'The inflation-adjusted yield on 10-year Treasury securities, derived from nominal yields minus market-implied inflation expectations.',
    
    calculation: {
      formula: 'DGS10 − T10YIE',
      explanation: '10-Year Treasury Constant Maturity Rate minus 10-Year Breakeven Inflation Rate.',
      components: [
        { id: 'DGS10', name: '10-Year Treasury Yield', role: 'Nominal long-term risk-free rate' },
        { id: 'T10YIE', name: '10-Year Breakeven Inflation', role: 'Market-implied inflation expectation' },
      ],
    },
    
    whyItMatters: 'The real rate is the true cost of capital for the economy, stripping out inflation expectations to reveal the actual return savers receive and borrowers pay. Real rates drove extraordinary equity valuations in the 2010s (negative territory) and the 2022 shift to positive real rates represented a regime change with profound implications for long-duration assets.',
    
    interpretation: {
      deeply_negative: 'Below -1%. Financial repression. Strongly supportive of risk assets, real estate, gold.',
      mildly_negative: '-1% to 0%. Still accommodative but less extreme. Typical of late-cycle easy policy.',
      neutral: '0% to +1%. Balanced conditions. Historically normal range for non-crisis periods.',
      restrictive: '+1% to +2%. Meaningful competition from risk-free assets. Challenging for leveraged strategies.',
      very_restrictive: 'Above +2%. Significantly tight. Historically rare outside Volcker era.',
    },
    
    keyLevels: [
      { level: '0%', significance: 'Threshold between accommodative and restrictive' },
      { level: '+1.5%', significance: 'Meaningfully restrictive - historical median' },
      { level: '+2.5%', significance: 'Very restrictive - last seen 2006-2007' },
    ],
    
    tradingImplication: 'Real rate regime is critical for equity factor allocation. Negative real rates favor growth, long-duration, and low-quality. Positive real rates favor value, short-duration, and quality.',
    
    relatedSignals: [
      { id: 'REAL_5Y', relationship: 'Shorter-term real rate, more Fed-sensitive' },
      { id: 'DGS10', relationship: 'Component - nominal rate' },
      { id: 'T10YIE', relationship: 'Component - inflation expectation' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Federal Reserve H.15, FRED',
      lag: 'Same day',
      history: '2003-01-02 (TIPS-derived breakevens)',
      units: 'Percentage',
    },
  },

  REAL_5Y: {
    id: 'REAL_5Y',
    name: '5-Year Real Interest Rate',
    shortName: 'Real 5Y',
    category: 'rates',
    tier: 'primary',
    
    description: 'The inflation-adjusted yield on 5-year Treasury securities, representing real borrowing costs at the intermediate tenor most sensitive to Fed policy expectations.',
    
    calculation: {
      formula: 'DGS5 − T5YIE',
      components: [
        { id: 'DGS5', name: '5-Year Treasury Yield', role: 'Intermediate-term nominal rate' },
        { id: 'T5YIE', name: '5-Year Breakeven Inflation', role: 'Medium-term inflation expectation' },
      ],
    },
    
    whyItMatters: 'The 5-year tenor sits at the intersection of Fed policy transmission and term premium. It\'s considered the "cleanest" read on where markets expect the Fed to ultimately land in real terms.',
    
    relatedSignals: [
      { id: 'REAL_10Y', relationship: 'Longer-term real rate' },
      { id: 'DGS5', relationship: 'Component - nominal rate' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Federal Reserve H.15, FRED',
      lag: 'Same day',
      history: '2003-01-02',
      units: 'Percentage',
    },
  },

  TERM_PREMIUM_PROXY: {
    id: 'TERM_PREMIUM_PROXY',
    name: 'Term Premium Proxy',
    shortName: 'Term Premium',
    category: 'rates',
    tier: 'primary',
    
    description: 'An approximation of the term premium embedded in long-term Treasury yields, representing compensation for duration risk beyond expected future short rates.',
    
    calculation: {
      formula: 'DGS10 − (Average of forward short rate expectations)',
      explanation: 'Derived from yield curve models decomposing long-term rates into expectations and risk premium components.',
    },
    
    whyItMatters: 'Term premium captures the extra yield investors demand for holding long-duration bonds versus rolling short-term bills. QE compressed term premium to negative levels; QT and fiscal concerns can push it higher. Term premium dynamics often explain yield moves that seem disconnected from Fed policy expectations.',
    
    interpretation: {
      negative: 'QE effects dominant. Investors accepting less compensation for duration risk. Unusual historically.',
      low_positive: '0-50bps. Normal range. Some duration compensation but not excessive.',
      elevated: '100bps+. Significant duration risk premium. Often reflects fiscal concerns or inflation uncertainty.',
    },
    
    relatedSignals: [
      { id: 'DGS10', relationship: 'Long rate containing term premium' },
      { id: 'CURVE_2S10S', relationship: 'Related curve measure' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Derived calculation',
      units: 'Basis points',
    },
  },

  CURVE_3M10Y: {
    id: 'CURVE_3M10Y',
    name: '3-Month to 10-Year Curve Spread',
    shortName: '3M10Y Curve',
    category: 'rates',
    tier: 'primary',
    
    description: 'The spread between 10-year Treasury yield and 3-month Treasury bill rate, an alternative curve measure preferred by NY Fed recession probability models.',
    
    calculation: {
      formula: 'DGS10 − DTB3',
      components: [
        { id: 'DGS10', name: '10-Year Treasury Yield', role: 'Long-term rate' },
        { id: 'DTB3', name: '3-Month Treasury Bill', role: 'Very short-term rate, close to fed funds' },
      ],
    },
    
    whyItMatters: 'The 3M10Y spread is the input to the NY Fed\'s recession probability model. Some argue it provides a cleaner signal than 2s10s because the 3-month rate is more directly tied to current Fed policy, while the 2-year rate includes expectations for future policy.',
    
    interpretation: {
      positive: 'Normal curve. Economic expansion expected. NY Fed recession probability low.',
      flat: 'Late cycle. Fed policy restrictive relative to long-term expectations.',
      inverted: 'Recession warning. NY Fed model probability rises significantly with sustained inversion.',
    },
    
    relatedSignals: [
      { id: 'CURVE_2S10S', relationship: 'Alternative curve measure' },
      { id: 'DGS10', relationship: 'Component - long end' },
      { id: 'DTB3', relationship: 'Component - short end' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Federal Reserve H.15 via FRED',
      lag: 'Same day',
      history: '1982-01-04',
      units: 'Percentage points',
    },
  },

  CCC_B_SPREAD: {
    id: 'CCC_B_SPREAD',
    name: 'CCC minus B Rated Spread',
    shortName: 'CCC-B Spread',
    category: 'credit',
    tier: 'primary',
    
    description: 'The spread differential between CCC-rated (lowest quality, highest risk) and B-rated high yield bonds, measuring stress within the junk bond universe.',
    
    whyItMatters: 'CCC spreads are the canary in the credit coal mine. When distress builds in credit markets, CCC bonds sell off first as investors flee the riskiest tier. This spread widening often precedes broader HY stress and can signal approaching default cycles.',
    
    interpretation: {
      tight: 'Below 300bps. Aggressive risk appetite extending to lowest-quality credits.',
      normal: '300-500bps. Normal differentiation between quality tiers.',
      elevated: '500-800bps. Investors discriminating against weakest credits. Early stress.',
      stressed: 'Above 800bps. Default concerns acute. Credit cycle turning.',
    },
    
    relatedSignals: [
      { id: 'BAMLH0A0HYM2', relationship: 'Broader HY spread' },
      { id: 'HY_IG_SPREAD', relationship: 'HY vs IG comparison' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'ICE BofA Indices via FRED',
      lag: 'Same day',
      units: 'Basis points',
    },
  },

  BBB_TREASURY_SPREAD: {
    id: 'BBB_TREASURY_SPREAD',
    name: 'BBB Corporate − Treasury Spread',
    shortName: 'BBB Spread',
    category: 'credit',
    tier: 'primary',
    
    description: 'The option-adjusted spread on BBB-rated corporate bonds versus Treasury securities, measuring the risk premium on the lowest investment-grade tier.',
    
    whyItMatters: 'BBB is the largest segment of investment grade credit and sits on the "cliff" above junk status. Widening BBB spreads can signal: (1) rising downgrade risk ("fallen angels"), (2) general credit concern, or (3) liquidity stress in the corporate bond market. The BBB segment has grown significantly post-2008, making this metric increasingly important.',
    
    interpretation: {
      tight: 'Below 120bps. Aggressive IG market. Low default/downgrade concern.',
      normal: '120-180bps. Healthy IG differentiation.',
      elevated: '180-250bps. Credit concerns building. Downgrade risk priced.',
      stressed: 'Above 250bps. Significant distress. Fallen angel risk elevated.',
    },
    
    watchFor: [
      'Sector concentration - any sector stress amplified by BBB weight',
      'Leverage trends in BBB universe',
      'Fed IG facilities - backstop reduces tail risk',
    ],
    
    relatedSignals: [
      { id: 'BAMLC0A0CM', relationship: 'Broader IG index spread' },
      { id: 'HY_IG_SPREAD', relationship: 'IG to HY quality jump' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'ICE BofA Indices via FRED',
      lag: 'Same day',
      history: '1996-12-31',
      units: 'Basis points',
    },
  },

  SOFR_EFFR_SPREAD: {
    id: 'SOFR_EFFR_SPREAD',
    name: 'SOFR − Effective Fed Funds Spread',
    shortName: 'SOFR-EFFR Spread',
    category: 'funding',
    tier: 'primary',
    
    description: 'The spread between the Secured Overnight Financing Rate (SOFR) and the Effective Federal Funds Rate (EFFR), measuring the relationship between secured (repo) and unsecured overnight funding markets.',
    
    calculation: {
      formula: 'SOFR − EFFR',
      components: [
        { id: 'SOFR', name: 'Secured Overnight Financing Rate', role: 'Treasury repo market rate' },
        { id: 'EFFR', name: 'Effective Federal Funds Rate', role: 'Interbank unsecured rate' },
      ],
    },
    
    whyItMatters: 'This spread reveals plumbing stress in overnight funding markets. Normally, SOFR trades slightly below EFFR because it\'s secured by Treasury collateral. When SOFR spikes above EFFR, it signals collateral scarcity, repo market stress, or balance sheet constraints. The September 2019 repo spike was a vivid demonstration - SOFR exploded to 5%+ while EFFR stayed anchored.',
    
    interpretation: {
      normal_negative: '-5bps to 0bps. Normal relationship. Healthy funding markets.',
      tight: '0bps to +10bps. Mild pressure. Often seen at quarter-end.',
      elevated: '+10bps to +25bps. Meaningful funding stress. Reserve issues.',
      crisis: 'Above +25bps. Severe funding market disruption. Fed intervention likely.',
    },
    
    historicalEvents: [
      { date: '2019-09-17', event: 'Repo market spike', impact: 'SOFR hit 5.25%, Fed launched repo operations' },
      { date: '2020-03', event: 'COVID dash for cash', impact: 'Brief SOFR spike before Fed unlimited repo' },
    ],
    
    relatedSignals: [
      { id: 'EFFR_IORB_SPREAD', relationship: 'Related funding market indicator' },
      { id: 'RRPONTSYD', relationship: 'RRP usage affects funding dynamics' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'NY Fed, Federal Reserve',
      lag: 'Same day',
      history: '2018-04-03 (SOFR inception)',
      units: 'Basis points',
    },
  },

  EFFR_IORB_SPREAD: {
    id: 'EFFR_IORB_SPREAD',
    name: 'EFFR − Interest on Reserve Balances Spread',
    shortName: 'EFFR-IORB Spread',
    category: 'funding',
    tier: 'primary',
    
    description: 'The spread between the Effective Federal Funds Rate and the Interest on Reserve Balances rate, measuring how fed funds trades relative to the Fed\'s administered rate.',
    
    calculation: {
      formula: 'EFFR − IORB',
      components: [
        { id: 'EFFR', name: 'Effective Federal Funds Rate', role: 'Market-determined overnight rate' },
        { id: 'IORB', name: 'Interest on Reserve Balances', role: 'Fed\'s administered rate on bank reserves' },
      ],
    },
    
    whyItMatters: 'IORB is supposed to be the "floor" of the fed funds corridor. When EFFR trades significantly below IORB, it indicates non-bank lending (GSEs, FHLBs) or arbitrage frictions. When EFFR approaches or exceeds IORB, it signals potential reserve scarcity. This spread is a key diagnostic for Fed operating framework.',
    
    interpretation: {
      normal: '-8bps to -5bps. EFFR below IORB as expected. Normal arbitrage friction.',
      tight_to_iorb: '-5bps to 0bps. EFFR approaching IORB. Reserve conditions may be tightening.',
      at_or_above_iorb: '0bps or positive. Unusual - EFFR at or above IORB suggests reserve scarcity.',
    },
    
    relatedSignals: [
      { id: 'SOFR_EFFR_SPREAD', relationship: 'Related funding market indicator' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'Federal Reserve',
      lag: 'Same day',
      history: '2021-07-29 (IORB replaced IOER)',
      units: 'Basis points',
    },
  },
};


// =============================================================================
// DERIVED SIGNALS - Z-Scores (abbreviated set for key signals)
// =============================================================================

const ZSCORE_SIGNALS = {
  NET_LIQUIDITY_ZSCORE: {
    id: 'NET_LIQUIDITY_ZSCORE',
    name: 'Net Liquidity Z-Score',
    shortName: 'Net Liq Z',
    category: 'liquidity',
    tier: 'derived',
    
    description: 'Net Federal Liquidity normalized to standard deviations, providing regime-adjusted context for the primary liquidity measure.',
    
    calculation: {
      formula: '(NET_LIQUIDITY − μ) / σ',
      underlyingSignal: { id: 'NET_LIQUIDITY', name: 'Net Federal Liquidity' },
    },
    
    whyItMatters: 'Because net liquidity has shifted dramatically across regimes (pre-2008, QE era, post-2020), the z-score helps identify whether current conditions are extreme relative to historical context.',
    
    interpretation: {
      very_negative: 'Z < -2. Extremely tight liquidity relative to history.',
      negative: 'Z -2 to -1. Below average liquidity. Headwind for risk assets.',
      neutral: 'Z -1 to +1. Near normal liquidity conditions.',
      positive: 'Z +1 to +2. Above average liquidity. Supportive of risk.',
      very_positive: 'Z > +2. Extremely elevated liquidity.',
    },
    
    relatedSignals: [
      { id: 'NET_LIQUIDITY', relationship: 'Underlying composite signal' },
      { id: 'NET_LIQUIDITY_ROC', relationship: 'Direction and momentum' },
    ],
    
    dataProperties: {
      frequency: 'Weekly',
      source: 'Derived from CAP NET_LIQUIDITY',
      units: 'Standard deviations',
    },
  },

  WALCL_ZSCORE: {
    id: 'WALCL_ZSCORE',
    name: 'Fed Balance Sheet Z-Score',
    shortName: 'Fed B/S Z',
    category: 'liquidity',
    tier: 'derived',
    description: 'Federal Reserve total assets normalized to standard deviations from historical mean.',
    calculation: { formula: '(WALCL − μ) / σ', underlyingSignal: { id: 'WALCL' } },
    whyItMatters: 'Contextualizes current balance sheet size against history, revealing whether we\'re in historically expansive or restrictive territory.',
    relatedSignals: [{ id: 'WALCL', relationship: 'Underlying raw series' }],
    dataProperties: { frequency: 'Weekly', source: 'Derived from FRED WALCL', units: 'Standard deviations' },
  },

  VIXCLS_ZSCORE: {
    id: 'VIXCLS_ZSCORE',
    name: 'VIX Z-Score',
    shortName: 'VIX Z',
    category: 'volatility',
    tier: 'derived',
    description: 'VIX normalized to standard deviations, showing whether current volatility is historically extreme.',
    calculation: { formula: '(VIXCLS − μ) / σ', underlyingSignal: { id: 'VIXCLS' } },
    whyItMatters: 'VIX level interpretation depends on regime. Z-score of +2 indicates very elevated vol regardless of absolute level.',
    relatedSignals: [{ id: 'VIXCLS', relationship: 'Underlying VIX level' }],
    dataProperties: { frequency: 'Daily', source: 'Derived from FRED VIXCLS', units: 'Standard deviations' },
  },

  CURVE_2S10S_ZSCORE: {
    id: 'CURVE_2S10S_ZSCORE',
    name: '2s10s Curve Z-Score',
    shortName: '2s10s Z',
    category: 'rates',
    tier: 'derived',
    description: 'The 2s10s yield curve spread normalized to historical standard deviations.',
    calculation: { formula: '(CURVE_2S10S − μ) / σ', underlyingSignal: { id: 'CURVE_2S10S' } },
    whyItMatters: 'Identifies whether current curve shape is historically extreme, helping contextualize inversion severity.',
    relatedSignals: [{ id: 'CURVE_2S10S', relationship: 'Underlying spread' }],
    dataProperties: { frequency: 'Daily', source: 'Derived', units: 'Standard deviations' },
  },

  HY_IG_SPREAD_ZSCORE: {
    id: 'HY_IG_SPREAD_ZSCORE',
    name: 'HY-IG Spread Z-Score',
    shortName: 'HY-IG Z',
    category: 'credit',
    tier: 'derived',
    description: 'HY-IG spread normalized to historical distribution.',
    calculation: { formula: '(HY_IG_SPREAD − μ) / σ', underlyingSignal: { id: 'HY_IG_SPREAD' } },
    whyItMatters: 'Compares current credit risk appetite to history regardless of absolute spread levels.',
    relatedSignals: [{ id: 'HY_IG_SPREAD', relationship: 'Underlying spread' }],
    dataProperties: { frequency: 'Daily', source: 'Derived', units: 'Standard deviations' },
  },

  BAMLH0A0HYM2_ZSCORE: {
    id: 'BAMLH0A0HYM2_ZSCORE',
    name: 'High Yield Spread Z-Score',
    shortName: 'HY Spread Z',
    category: 'credit',
    tier: 'derived',
    description: 'High yield OAS normalized to standard deviations.',
    calculation: { formula: '(BAMLH0A0HYM2 − μ) / σ' },
    relatedSignals: [{ id: 'BAMLH0A0HYM2', relationship: 'Underlying spread' }],
    dataProperties: { frequency: 'Daily', units: 'Standard deviations' },
  },

  NFCI_ZSCORE: {
    id: 'NFCI_ZSCORE',
    name: 'Chicago Fed NFCI Z-Score',
    shortName: 'NFCI Z',
    category: 'macro',
    tier: 'derived',
    description: 'Chicago Fed National Financial Conditions Index normalized.',
    calculation: { formula: '(NFCI − μ) / σ', underlyingSignal: { id: 'NFCI' } },
    whyItMatters: 'NFCI is already standardized; this provides additional normalization for extreme readings.',
    relatedSignals: [{ id: 'NFCI', relationship: 'Underlying index' }],
    dataProperties: { frequency: 'Weekly', units: 'Standard deviations' },
  },

  UMCSENT_ZSCORE: {
    id: 'UMCSENT_ZSCORE',
    name: 'Consumer Sentiment Z-Score',
    shortName: 'Sentiment Z',
    category: 'macro',
    tier: 'derived',
    description: 'University of Michigan Consumer Sentiment normalized to historical standard deviations.',
    calculation: { formula: '(UMCSENT − μ) / σ', underlyingSignal: { id: 'UMCSENT' } },
    whyItMatters: 'Identifies when consumer mood is historically extreme - either euphoric or depressed.',
    interpretation: {
      very_negative: 'Z < -2. Consumer sentiment severely depressed. Recession-level readings.',
      very_positive: 'Z > +2. Euphoric sentiment. Often late-cycle.',
    },
    relatedSignals: [{ id: 'UMCSENT', relationship: 'Underlying index' }],
    dataProperties: { frequency: 'Monthly', units: 'Standard deviations' },
  },

  ICSA_ZSCORE: {
    id: 'ICSA_ZSCORE',
    name: 'Initial Claims Z-Score',
    shortName: 'Claims Z',
    category: 'labor',
    tier: 'derived',
    description: 'Weekly initial unemployment claims normalized to standard deviations.',
    calculation: { formula: '(ICSA − μ) / σ', underlyingSignal: { id: 'ICSA' } },
    whyItMatters: 'Identifies when layoffs are historically elevated (recession signal) or depressed (tight labor market).',
    relatedSignals: [{ id: 'ICSA', relationship: 'Underlying claims data' }],
    dataProperties: { frequency: 'Weekly', units: 'Standard deviations' },
  },
};


// =============================================================================
// DERIVED SIGNALS - Rate of Change
// =============================================================================

const ROC_SIGNALS = {
  NET_LIQUIDITY_ROC: {
    id: 'NET_LIQUIDITY_ROC',
    name: 'Net Liquidity Rate of Change',
    shortName: 'Net Liq ROC',
    category: 'liquidity',
    tier: 'derived',
    
    description: 'The rate of change in Net Federal Liquidity, capturing the momentum and direction of the primary liquidity measure.',
    
    calculation: {
      formula: '(NET_LIQt − NET_LIQt-n) / NET_LIQt-n × 100',
      underlyingSignal: { id: 'NET_LIQUIDITY' },
    },
    
    whyItMatters: 'Net Liquidity ROC is arguably the single most important momentum signal for macro-driven markets. The ROC can turn positive even during QT if TGA drawdowns or RRP declines offset balance sheet runoff.',
    
    interpretation: {
      strongly_positive: 'ROC > +10% (annualized). Rapid liquidity expansion. Very supportive of risk.',
      positive: 'ROC +3% to +10%. Moderate expansion. Tailwind for markets.',
      flat: 'ROC -3% to +3%. Stable liquidity. Neutral.',
      negative: 'ROC -3% to -10%. Liquidity contraction. Headwind.',
      strongly_negative: 'ROC < -10%. Rapid contraction. Significant stress potential.',
    },
    
    tradingImplication: 'Many quantitative strategies use Net Liquidity ROC as a regime signal. Positive ROC favors risk-on; negative ROC favors defensive positioning.',
    
    relatedSignals: [
      { id: 'NET_LIQUIDITY', relationship: 'Underlying level' },
      { id: 'WALCL_ROC', relationship: 'Fed balance sheet momentum' },
    ],
    
    dataProperties: { frequency: 'Weekly', units: 'Percentage' },
  },

  WALCL_ROC: {
    id: 'WALCL_ROC',
    name: 'Fed Balance Sheet Rate of Change',
    shortName: 'Fed B/S ROC',
    category: 'liquidity',
    tier: 'derived',
    description: 'Rate of change in Fed total assets, measuring QE/QT pace.',
    calculation: { formula: '(WALCLt − WALCLt-n) / WALCLt-n × 100' },
    whyItMatters: 'Direction often matters more than level. Positive ROC = QE conditions; Negative ROC = QT.',
    relatedSignals: [{ id: 'WALCL', relationship: 'Underlying level' }],
    dataProperties: { frequency: 'Weekly', units: 'Percentage' },
  },

  VIXCLS_ROC: {
    id: 'VIXCLS_ROC',
    name: 'VIX Rate of Change',
    shortName: 'VIX ROC',
    category: 'volatility',
    tier: 'derived',
    description: 'Rate of change in VIX, capturing volatility momentum.',
    calculation: { formula: '(VIXt − VIXt-n) / VIXt-n × 100' },
    whyItMatters: 'VIX moves are asymmetric - spikes are fast, declines are slow. ROC captures this momentum.',
    relatedSignals: [{ id: 'VIXCLS', relationship: 'Underlying VIX' }],
    dataProperties: { frequency: 'Daily', units: 'Percentage' },
  },
};


// =============================================================================
// FRED SOURCE DATA (Core series)
// =============================================================================

const SOURCE_SIGNALS = {
  WALCL: {
    id: 'WALCL',
    name: 'Federal Reserve Total Assets',
    shortName: 'Fed Balance Sheet',
    category: 'liquidity',
    tier: 'source',
    fredSeriesId: 'WALCL',
    
    description: 'All assets held by the Federal Reserve System, including securities, loans, and other assets. The primary tool of quantitative easing (QE) and quantitative tightening (QT).',
    
    whyItMatters: 'The Fed balance sheet is the foundation of modern monetary policy. QE expands the balance sheet by purchasing securities, injecting reserves into the banking system. QT shrinks it by allowing securities to mature without reinvestment, draining reserves.',
    
    historicalContext: {
      preGFC: 'Under $900 billion - primarily Treasury holdings',
      gfc: 'Expanded to $2.2 trillion through QE1',
      qe2_qe3: 'Grew to $4.5 trillion by 2014',
      qe4: 'Pandemic response pushed to $9 trillion by 2022',
      qt: 'Currently contracting via balance sheet runoff',
    },
    
    interpretation: {
      expanding: 'Balance sheet growth = liquidity injection. Supportive of risk assets.',
      stable: 'Neutral liquidity posture.',
      contracting: 'Balance sheet shrinkage = liquidity withdrawal. Headwind for risk assets.',
    },
    
    relatedSignals: [
      { id: 'WALCL_ZSCORE', relationship: 'Normalized level' },
      { id: 'WALCL_ROC', relationship: 'Rate of change' },
      { id: 'NET_LIQUIDITY', relationship: 'WALCL is primary component' },
    ],
    
    dataProperties: {
      frequency: 'Weekly (Wednesday)',
      release: 'H.4.1 Statistical Release',
      lag: '1 day',
      history: '1945-present',
      units: 'Millions of USD',
    },
  },

  WTREGEN: {
    id: 'WTREGEN',
    name: 'Treasury General Account',
    shortName: 'TGA',
    category: 'liquidity',
    tier: 'source',
    fredSeriesId: 'WTREGEN',
    
    description: 'The U.S. Treasury\'s operating cash balance held at the Federal Reserve. This is the government\'s primary checking account.',
    
    whyItMatters: 'TGA changes directly affect bank reserves and hence liquidity. When Treasury spends (TGA falls), cash flows to recipients\' bank accounts, increasing reserves. When Treasury collects taxes or issues debt (TGA rises), the reverse occurs.',
    
    interpretation: {
      falling: 'Treasury spending down TGA. Liquidity injection.',
      rising: 'Treasury building cash. Liquidity drain.',
      extreme_low: 'Near debt ceiling exhaustion. Extraordinary measures in effect.',
      extreme_high: 'Large cash buffer. Liquidity parked out of the system.',
    },
    
    relatedSignals: [
      { id: 'NET_LIQUIDITY', relationship: 'TGA is subtracted (drain)' },
    ],
    
    dataProperties: {
      frequency: 'Weekly (Wednesday)',
      release: 'H.4.1 Statistical Release',
      lag: '1 day',
      history: '1974-present',
      units: 'Millions of USD',
    },
  },

  RRPONTSYD: {
    id: 'RRPONTSYD',
    name: 'Overnight Reverse Repurchase Agreements',
    shortName: 'RRP / ON RRP',
    category: 'liquidity',
    tier: 'source',
    fredSeriesId: 'RRPONTSYD',
    
    description: 'Cash parked at the Fed through the Overnight Reverse Repo Facility. Counterparties lend cash to the Fed overnight, receiving Treasury collateral.',
    
    whyItMatters: 'RRP represents liquidity removed from the financial system. Money parked at the Fed isn\'t circulating in markets. The facility exploded from near-zero pre-2021 to over $2 trillion by 2022.',
    
    historicalContext: {
      prePandemic: 'Near zero - facility existed but rarely used',
      pandemic: 'Began rising mid-2021 as QE created excess liquidity',
      peak: 'Over $2.5 trillion in December 2022',
      current: 'Declining as T-bill supply increases and QT continues',
    },
    
    relatedSignals: [
      { id: 'NET_LIQUIDITY', relationship: 'RRP is subtracted (sterilization)' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      release: 'NY Fed Open Market Operations',
      lag: 'Same day',
      history: '2003-present',
      units: 'Billions of USD',
    },
  },

  VIXCLS: {
    id: 'VIXCLS',
    name: 'CBOE Volatility Index',
    shortName: 'VIX',
    category: 'volatility',
    tier: 'source',
    fredSeriesId: 'VIXCLS',
    
    description: 'The "fear gauge" - measures expected 30-day volatility of the S&P 500 derived from options prices.',
    
    whyItMatters: 'VIX is the most-watched volatility measure globally. Low VIX indicates complacency; high VIX indicates fear. VIX tends to spike during selloffs and grind lower during rallies.',
    
    interpretation: {
      low: 'VIX < 15. Calm/complacent. Often late-cycle.',
      normal: 'VIX 15-20. Healthy volatility regime.',
      elevated: 'VIX 20-30. Heightened uncertainty.',
      high: 'VIX 30-40. Fear elevated. Selloff likely underway.',
      extreme: 'VIX > 40. Crisis conditions. 2008, 2020 peaks.',
    },
    
    keyLevels: [
      { level: '12', significance: 'Extreme complacency' },
      { level: '20', significance: 'Long-term average' },
      { level: '30', significance: 'Elevated fear' },
      { level: '40', significance: 'Crisis threshold' },
    ],
    
    relatedSignals: [
      { id: 'VIX_TERM_SPREAD', relationship: 'Term structure context' },
      { id: 'VXVCLS', relationship: 'Volatility of volatility' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'CBOE via FRED',
      lag: 'Same day',
      history: '1990-01-02',
      units: 'Percentage',
    },
  },

  BAMLH0A0HYM2: {
    id: 'BAMLH0A0HYM2',
    name: 'ICE BofA US High Yield OAS',
    shortName: 'HY OAS',
    category: 'credit',
    tier: 'source',
    fredSeriesId: 'BAMLH0A0HYM2',
    
    description: 'Option-adjusted spread on the ICE BofA US High Yield Index, measuring the credit premium on junk bonds.',
    
    whyItMatters: 'HY spreads are a direct measure of credit market risk appetite. Tight spreads = risk-on; wide spreads = stress.',
    
    interpretation: {
      tight: 'Below 350bps. Aggressive risk appetite.',
      normal: '350-500bps. Healthy credit conditions.',
      elevated: '500-700bps. Credit concerns.',
      stressed: 'Above 700bps. Crisis territory.',
    },
    
    relatedSignals: [
      { id: 'HY_IG_SPREAD', relationship: 'HY vs IG comparison' },
    ],
    
    dataProperties: {
      frequency: 'Daily',
      source: 'ICE BofA via FRED',
      history: '1996-12-31',
      units: 'Basis points',
    },
  },

  NFCI: {
    id: 'NFCI',
    name: 'Chicago Fed National Financial Conditions Index',
    shortName: 'NFCI',
    category: 'macro',
    tier: 'source',
    fredSeriesId: 'NFCI',
    
    description: 'A comprehensive weekly index measuring financial conditions in money markets, debt and equity markets, and the shadow banking system.',
    
    whyItMatters: 'NFCI aggregates 105 measures of financial activity. Negative = looser than average; Positive = tighter than average.',
    
    interpretation: {
      negative: 'Looser than average financial conditions',
      zero: 'Average conditions',
      positive: 'Tighter than average financial conditions',
      stressThreshold: 'Values above +0.5 indicate meaningful stress',
    },
    
    relatedSignals: [
      { id: 'STLFSI4', relationship: 'Alternative stress index' },
    ],
    
    dataProperties: {
      frequency: 'Weekly',
      source: 'Chicago Fed',
      history: '1971-01-08',
      units: 'Index (standard deviations from mean)',
    },
  },

  UMCSENT: {
    id: 'UMCSENT',
    name: 'University of Michigan Consumer Sentiment',
    shortName: 'UMich Sentiment',
    category: 'macro',
    tier: 'source',
    fredSeriesId: 'UMCSENT',
    
    description: 'Survey-based measure of consumer confidence regarding personal finances and business conditions.',
    
    whyItMatters: 'Consumer sentiment is a leading indicator for consumer spending, which comprises ~70% of US GDP.',
    
    historicalRange: {
      historicAvg: 'Approximately 85-90',
      expansionHigh: '100+ during economic booms',
      recessionLow: '60-70 during downturns',
      allTimeLow: '50.0 in June 2022 (inflation shock)',
    },
    
    dataProperties: {
      frequency: 'Monthly',
      source: 'University of Michigan',
      history: '1952-11-01',
      units: 'Index (1966=100)',
    },
  },

  ICSA: {
    id: 'ICSA',
    name: 'Initial Claims',
    shortName: 'Jobless Claims',
    category: 'labor',
    tier: 'source',
    fredSeriesId: 'ICSA',
    
    description: 'The number of new filings for unemployment insurance benefits during the reference week. The timeliest labor market indicator.',
    
    whyItMatters: 'Initial claims provide the earliest signal of labor market deterioration. Rising claims = layoffs increasing.',
    
    keyLevels: [
      { level: '200,000', significance: 'Very tight labor market' },
      { level: '250,000', significance: 'Normal healthy range' },
      { level: '300,000', significance: 'Labor market cooling' },
      { level: '400,000', significance: 'Recessionary territory' },
    ],
    
    dataProperties: {
      frequency: 'Weekly (Thursday release)',
      source: 'Department of Labor',
      history: '1967-01-07',
      units: 'Number of claims',
    },
  },

  DGS10: {
    id: 'DGS10',
    name: '10-Year Treasury Constant Maturity',
    shortName: '10Y Yield',
    category: 'rates',
    tier: 'source',
    fredSeriesId: 'DGS10',
    description: 'The benchmark long-term US government bond yield.',
    whyItMatters: 'The 10-year yield is the most important rate in finance - affects mortgages, corporate borrowing, equity valuations.',
    dataProperties: { frequency: 'Daily', source: 'Federal Reserve H.15', units: 'Percentage' },
  },

  DGS2: {
    id: 'DGS2',
    name: '2-Year Treasury Constant Maturity',
    shortName: '2Y Yield',
    category: 'rates',
    tier: 'source',
    fredSeriesId: 'DGS2',
    description: 'The 2-year Treasury yield, most sensitive to Fed policy expectations.',
    whyItMatters: 'The 2-year yield is the market\'s best guess at where the Fed will be in 2 years.',
    dataProperties: { frequency: 'Daily', source: 'Federal Reserve H.15', units: 'Percentage' },
  },
};


// =============================================================================
// COMBINE ALL SIGNALS
// =============================================================================

/** @type {Object.<string, SignalDoc>} */
export const SIGNAL_DOCS = {
  ...FLAGSHIP_SIGNALS,
  ...PRIMARY_SIGNALS,
  ...ZSCORE_SIGNALS,
  ...ROC_SIGNALS,
  ...SOURCE_SIGNALS,
};


// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get documentation for a signal by ID
 * @param {string} signalId - The signal identifier
 * @returns {Object|null} Signal documentation or null if not found
 */
export function getSignalDoc(signalId) {
  return SIGNAL_DOCS[signalId] || null;
}

/**
 * Check if signal has documentation
 * @param {string} signalId - The signal identifier
 * @returns {boolean}
 */
export function hasSignalDoc(signalId) {
  return signalId in SIGNAL_DOCS;
}

/**
 * Get signals by tier
 * @param {'flagship'|'primary'|'derived'|'source'} tier
 * @returns {Object[]} Array of signal docs matching the tier
 */
export function getSignalsByTier(tier) {
  return Object.values(SIGNAL_DOCS).filter(doc => doc.tier === tier);
}

/**
 * Get signals by category
 * @param {string} category - liquidity, rates, credit, volatility, macro, etc.
 * @returns {Object[]} Array of signal docs matching the category
 */
export function getSignalsByCategory(category) {
  return Object.values(SIGNAL_DOCS).filter(doc => doc.category === category);
}

/**
 * Get all flagship signals
 * @returns {Object[]}
 */
export function getFlagshipSignals() {
  return getSignalsByTier('flagship');
}

/**
 * Search signals by name or description
 * @param {string} query - Search query
 * @returns {Object[]} Matching signal docs
 */
export function searchSignalDocs(query) {
  const q = query.toLowerCase();
  return Object.values(SIGNAL_DOCS).filter(doc =>
    doc.id.toLowerCase().includes(q) ||
    doc.name.toLowerCase().includes(q) ||
    (doc.shortName && doc.shortName.toLowerCase().includes(q)) ||
    (doc.description && doc.description.toLowerCase().includes(q))
  );
}

/**
 * Get the display tier label
 * @param {string} tier
 * @returns {string}
 */
export function getTierLabel(tier) {
  const labels = {
    flagship: '⚡ Flagship',
    primary: '📐 Primary',
    derived: '🔢 Derived',
    source: '📊 FRED',
  };
  return labels[tier] || tier;
}

/**
 * Get related signals for a given signal
 * @param {string} signalId
 * @returns {Object[]} Array of { id, relationship, doc }
 */
export function getRelatedSignals(signalId) {
  const doc = getSignalDoc(signalId);
  if (!doc?.relatedSignals) return [];
  
  return doc.relatedSignals.map(rel => ({
    ...rel,
    doc: getSignalDoc(rel.id),
  }));
}

// Export for debugging
export const _collections = {
  FLAGSHIP_SIGNALS,
  PRIMARY_SIGNALS,
  ZSCORE_SIGNALS,
  ROC_SIGNALS,
  SOURCE_SIGNALS,
};
